/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Badge } from '@/components/ui/badge';

export type FreshnessStatus = 'fresh' | 'stale' | 'outdated' | 'deprecated';

interface FreshnessIndicatorProps {
  status: FreshnessStatus;
  lastUpdated?: string;
}

export function FreshnessIndicator({ status, lastUpdated }: FreshnessIndicatorProps) {
  const config: Record<FreshnessStatus, { icon: string; label: string; className: string }> = {
    fresh: { icon: '🟢', label: 'Fresh', className: 'bg-green-100 text-green-800 border-green-200' },
    stale: { icon: '🟡', label: 'Stale', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    outdated: { icon: '🔴', label: 'Outdated', className: 'bg-red-100 text-red-800 border-red-200' },
    deprecated: { icon: '⚫', label: 'Deprecated', className: 'bg-gray-100 text-gray-800 border-gray-200' },
  };

  // Handle unexpected status values gracefully
  const statusConfig = config[status] ?? config.fresh;
  const { icon, label, className } = statusConfig;
  
  // Format relative time if lastUpdated is provided
  let relativeTime = '';
  if (lastUpdated) {
    const date = new Date(lastUpdated);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) relativeTime = 'today';
    else if (diffDays === 1) relativeTime = '1d';
    else if (diffDays < 30) relativeTime = `${diffDays}d`;
    else if (diffDays < 365) relativeTime = `${Math.floor(diffDays / 30)}mo`;
    else relativeTime = `${Math.floor(diffDays / 365)}y`;
  }

  return (
    <Badge variant="outline" className={`text-xs ${className}`}>
      <span className="mr-1">{icon}</span>
      {label}
      {relativeTime && <span className="ml-1 opacity-70">({relativeTime})</span>}
    </Badge>
  );
}
